/**
 * ClinicReportDetail — детальные отчёты выбранной клиники: сводка-карточки,
 * динамика выручки, врачи, топ услуг. Четыре независимых запроса за период.
 */

import {useQuery} from '@tanstack/react-query';

import {
  getClinicDoctors,
  getClinicRevenue,
  getClinicServices,
  getClinicSummary,
  type ReportPeriod,
  revenueGroupBy,
} from '@services/reports';
import {keys} from '@queries/keys';
import {Loader} from '@components/ui/Loader';

import {ClinicSummaryCards} from '../ClinicSummaryCards';
import {DoctorsReportTable} from '../DoctorsReportTable';
import {RevenueChart} from '../RevenueChart';
import {ServicesReportTable} from '../ServicesReportTable';

type Props = {
  clinicId: string;
  period: ReportPeriod;
};

export const ClinicReportDetail = ({clinicId, period}: Props) => {
  const summaryQ = useQuery({
    queryKey: keys.reports.summary(clinicId, period),
    queryFn: () => getClinicSummary(clinicId, period),
  });
  const revenueQ = useQuery({
    queryKey: keys.reports.revenue(clinicId, period),
    queryFn: () => getClinicRevenue(clinicId, period),
  });
  const doctorsQ = useQuery({
    queryKey: keys.reports.doctors(clinicId, period),
    queryFn: () => getClinicDoctors(clinicId, period),
  });
  const servicesQ = useQuery({
    queryKey: keys.reports.services(clinicId, period),
    queryFn: () => getClinicServices(clinicId, period),
  });

  if (summaryQ.isLoading || !summaryQ.data) {
    return (
      <div className="flex min-h-[360px] items-center justify-center">
        <Loader label="Считаем отчёты…" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <ClinicSummaryCards s={summaryQ.data} />

      {revenueQ.data !== undefined && (
        <RevenueChart points={revenueQ.data} groupBy={revenueGroupBy(period)} />
      )}

      <div className="grid grid-cols-[1.4fr_1fr] items-start gap-4 max-lg:grid-cols-1">
        {doctorsQ.data !== undefined && <DoctorsReportTable rows={doctorsQ.data} />}
        {servicesQ.data !== undefined && <ServicesReportTable rows={servicesQ.data} />}
      </div>
    </div>
  );
};
